import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, Star, ShieldCheck, BadgeCheck } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import JsonLd from "@/components/JsonLd";
import { SITE, SITE_URL, OFFICE_HERO, imgSrcSet } from "@/lib/site";
import { DMC_REGIONS, CITY_REGIONS } from "@/lib/dmcData";
import { Reveal, MaskLines, Overline } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import EnquiryForm from "@/components/EnquiryForm";
import { useCms } from "@/context/ContentContext";
import { dedupeBySlug, slugify } from "@/lib/cms";
import {
  DESTINATIONS, PACKAGES, MANIFESTO, STATS, TESTIMONIALS, REVIEW_AGGREGATE,
  HERO_IMAGES, MANIFESTO_IMAGE,
} from "@/lib/data";
import { getFeaturedGuides } from "@/lib/relatedGuides";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.45, 0.8]);

  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % HERO_IMAGES.length), 6000);
    return () => clearInterval(t);
  }, []);

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[640px] overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute inset-0">
        <AnimatePresence>
          <motion.img
            key={idx}
            src={HERO_IMAGES[idx].src}
            srcSet={imgSrcSet(HERO_IMAGES[idx].src)}
            sizes="100vw"
            alt={`${HERO_IMAGES[idx].place} — Himalayan landscape`}
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1.16 }}
            exit={{ opacity: 0 }}
            transition={{ opacity: { duration: 1.5, ease: "easeInOut" }, scale: { duration: 7.2, ease: "linear" } }}
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover"
          />
        </AnimatePresence>
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-[var(--tx-bg)]" />
      <div className="absolute inset-0 bg-gradient-to-t from-[var(--tx-bg)] via-transparent to-[var(--tx-bg)]/30" />

      {/* Rotating place label */}
      <div className="absolute top-28 right-8 z-20 hidden md:flex items-center gap-3">
        <AnimatePresence mode="wait">
          <motion.span
            key={idx}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.6 }}
            className="text-xs uppercase tracking-[0.28em] text-[var(--tx-text)]/80"
          >
            {HERO_IMAGES[idx].place}
          </motion.span>
        </AnimatePresence>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-6 right-8 z-20 flex items-center gap-2">
        {HERO_IMAGES.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            aria-label={`Show slide ${i + 1}`}
            data-testid={`hero-dot-${i}`}
            className="grid place-items-center p-2 -m-0.5"
          >
            <span className={`block h-1.5 rounded-full transition-all duration-500 ${i === idx ? "w-8 bg-[var(--tx-gold)]" : "w-3 bg-white/40 hover:bg-white/70"}`} />
          </button>
        ))}
      </div>

      <div className="relative z-10 flex h-full flex-col justify-end">
        <div className="mx-auto w-full max-w-[1400px] px-6 md:px-10 pb-16 md:pb-24">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
            <Overline>{`B2B DMC · For Travel Agents & Tour Operators`}</Overline>
          </motion.div>

          <h1 className="mt-6 font-display font-light tracking-tight text-[var(--tx-text)] text-5xl sm:text-7xl lg:text-[7.5rem] leading-[0.92]">
            <MaskLines lines={["Journeys into the", "unmapped Himalaya"]} delay={0.35} />
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9 }}
            className="mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-8"
          >
            <p className="max-w-md text-base md:text-lg text-[var(--tx-text)]/80 leading-relaxed">
              A <span className="text-[var(--tx-gold)]">B2B-only</span> destination management
              company for travel agents &amp; tour operators. We plan and run trips across
              all eight North East states, Bhutan, Nepal and the Darjeeling hills — you keep
              the client, we handle the ground.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Link to="/contact" data-testid="hero-net-rates" className="group inline-flex items-center gap-2 rounded-full bg-[var(--tx-gold)] px-7 py-3.5 text-sm font-medium text-[var(--tx-bg)] hover:bg-[var(--tx-text)] transition-colors duration-300">
                Get net rates
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link to="/packages" data-testid="hero-explore" className="inline-flex items-center rounded-full border border-white/25 px-7 py-3.5 text-sm text-[var(--tx-text)] hover:border-[var(--tx-gold)] hover:text-[var(--tx-gold)] transition-colors duration-300">
                Explore Journeys
              </Link>
            </div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--tx-text)]/55" data-testid="hero-trust-line">
              Since 2016 · GST &amp; Udyam/MSME registered · Offices: Siliguri &amp; Guwahati
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Stats() {
  return (
    <section className="border-b border-[var(--tx-border)] bg-[var(--tx-bg-2)]">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 divide-x divide-[var(--tx-border)]">
        {STATS.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.08} className="py-10 px-6 text-center md:text-left">
            <div className="font-display text-4xl md:text-5xl font-light text-[var(--tx-gold)]">{s.value}</div>
            <div className="mt-2 text-xs uppercase tracking-[0.18em] text-[var(--tx-text-2)]">{s.label}</div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Intro() {
  return (
    <section className="relative py-28 md:py-40" data-testid="intro-section">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid gap-12 lg:grid-cols-12 items-end">
        <div className="lg:col-span-7">
          <Reveal><Overline>Who we are</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display font-light tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              We don&apos;t sell tours. We design the North East, mountain by mountain, story by story.
            </h2>
          </Reveal>
        </div>
        <div className="lg:col-span-4 lg:col-start-9">
          <Reveal delay={0.2}>
            <p className="text-[var(--tx-text-2)] leading-relaxed text-lg">
              TripTrix Voyages is rooted in the Himalayas. From inner-line permits to
              hidden homestays, we open doors most agencies never find — and we stay
              on the ground with you, from first message to final farewell.
            </p>
            <Link to="/about" className="mt-8 inline-flex items-center gap-2 text-[var(--tx-gold)] hover:gap-3 transition-all duration-300">
              Read our story <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

// Restrained editorial mosaic: one tall feature row, one medium row, one
// three-up row — then the rhythm repeats. Mobile stays a simple single column.
const BENTO = [
  { span: "lg:col-span-7", h: "lg:h-[520px]" },
  { span: "lg:col-span-5", h: "lg:h-[520px]" },
  { span: "lg:col-span-5", h: "lg:h-[400px]" },
  { span: "lg:col-span-7", h: "lg:h-[400px]" },
  { span: "lg:col-span-4", h: "lg:h-[360px]" },
  { span: "lg:col-span-4", h: "lg:h-[360px]" },
  { span: "lg:col-span-4", h: "lg:h-[360px]" },
];

function DestinationCard({ d, i, layout }) {
  return (
    <Reveal delay={(i % 2) * 0.1} className={`col-span-1 ${layout ? layout.span : ""}`}>
      <Link to={`/destinations/${d.slug}`} data-testid={`home-destination-${d.slug}`} className={`group relative block h-[340px] md:h-[420px] ${layout ? layout.h : ""} overflow-hidden`}>
        <img src={d.image} alt={d.name} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--tx-bg)] via-[var(--tx-bg)]/10 to-transparent" />
        <div className="absolute inset-0 ring-1 ring-inset ring-white/0 group-hover:ring-[var(--tx-gold)]/40 transition-[box-shadow] duration-500" />
        <div className="absolute bottom-0 left-0 p-7">
          <span className="text-xs uppercase tracking-[0.2em] text-[var(--tx-gold)]">{d.kicker}</span>
          <h3 className="mt-2 font-display text-3xl md:text-4xl font-light">{d.name}</h3>
          <p className="mt-2 max-w-sm text-sm text-[var(--tx-text)]/70 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-500">
            {d.blurb}
          </p>
        </div>
        <div className="absolute top-6 right-6 grid h-10 w-10 place-items-center rounded-full border border-white/25 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-500">
          <ArrowUpRight className="h-4 w-4" />
        </div>
      </Link>
    </Reveal>
  );
}

function Destinations() {
  const cms = useCms();
  const all = dedupeBySlug(DESTINATIONS, cms.destinations);
  const feat = cms.settings?.featuredDestinations || [];
  const list = feat.length
    ? feat.map((s) => all.find((x) => x.slug === s)).filter(Boolean)
    : DESTINATIONS;
  return (
    <section className="py-24 md:py-32 bg-[var(--tx-bg-2)]" data-testid="destinations-section">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <Reveal><Overline>Where we go</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display font-light tracking-tight text-4xl sm:text-5xl lg:text-6xl">
                Himalayan worlds, one mountain range
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <Link to="/destinations" className="inline-flex items-center gap-2 text-[var(--tx-gold)] hover:gap-3 transition-all duration-300">
              All destinations <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 md:gap-6">
          {list.map((d, i) => (<DestinationCard key={d.slug} d={d} i={i} layout={BENTO[i % BENTO.length]} />))}
        </div>
      </div>
    </section>
  );
}

function Manifesto() {
  return (
    <section className="relative py-28 md:py-40 overflow-hidden" data-testid="manifesto-section">
      <div className="absolute inset-0">
        <img src={MANIFESTO_IMAGE} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-[var(--tx-bg)]/80" />
      </div>
      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
        <Reveal><Overline>Why travel with TripTrix</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 mb-16 font-display font-light tracking-tight text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
            Four promises we make on the ground
          </h2>
        </Reveal>

        <div className="space-y-0">
          {MANIFESTO.map((m, i) => (
            <Reveal key={m.no} delay={i * 0.05}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 py-10 border-t border-[var(--tx-border)]">
                <div className="md:col-span-2 font-display text-4xl md:text-5xl font-light text-[var(--tx-gold)]">{m.no}</div>
                <h3 className="md:col-span-4 font-display text-2xl md:text-3xl font-light">{m.title}</h3>
                <p className="md:col-span-6 text-[var(--tx-text-2)] leading-relaxed text-lg">{m.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Packages() {
  const cms = useCms();
  const all = dedupeBySlug(PACKAGES, cms.packages);
  const feat = cms.settings?.featuredPackages || [];
  const list = feat.length
    ? feat.map((s) => all.find((p) => p.slug === s)).filter(Boolean)
    : PACKAGES.slice(0, 6);
  return (
    <section className="py-24 md:py-32" data-testid="packages-section">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <Reveal><Overline>Signature journeys</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display font-light tracking-tight text-4xl sm:text-5xl lg:text-6xl">
                Ready itineraries, built to tailor
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <Link to="/packages" className="inline-flex items-center gap-2 text-[var(--tx-gold)] hover:gap-3 transition-all duration-300">
              All journeys <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {list.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 3) * 0.08}>
              <div className="group h-full flex flex-col border border-[var(--tx-border)] bg-[var(--tx-bg-2)] overflow-hidden hover:border-[var(--tx-gold)]/40 transition-colors duration-500">
                <div className="relative h-56 overflow-hidden">
                  <img src={p.image} alt={p.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                  <span className="absolute top-4 left-4 rounded-full bg-[var(--tx-bg)]/80 backdrop-blur px-3 py-1 text-xs text-[var(--tx-text)]">{p.region}</span>
                  {feat.includes(p.slug) && (
                    <span data-testid={`popular-badge-${p.slug}`} className="absolute top-4 right-4 inline-flex items-center gap-1 rounded-full bg-[var(--tx-gold)] px-3 py-1 text-xs font-medium text-[var(--tx-bg)] shadow-lg">
                      <Star className="h-3 w-3 fill-current" /> Popular
                    </span>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <span className="text-xs uppercase tracking-[0.18em] text-[var(--tx-text-2)]">{p.days}</span>
                  <h3 className="mt-2 font-display text-2xl font-light">{p.title}</h3>
                  <p className="mt-3 text-sm text-[var(--tx-text-2)] leading-relaxed flex-1">{p.summary}</p>
                  <div className="mt-6 flex items-center justify-between pt-5 border-t border-[var(--tx-border)]">
                    <span className="text-sm text-[var(--tx-gold)]">Net rates on request</span>
                    <Link to={`/packages/${p.slug}`} className="inline-flex items-center gap-1.5 text-sm text-[var(--tx-text)] hover:text-[var(--tx-gold)] transition-colors duration-300">
                      View journey <ArrowUpRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const cms = useCms();
  const allReviews = dedupeBySlug(TESTIMONIALS.map((t) => ({ ...t, slug: t.slug || slugify(t.name || "") })), cms.reviews);
  const featured = allReviews.slice(0, 6);
  const reviewCount = REVIEW_AGGREGATE.reviewCount + (cms.reviews || []).length;
  return (
    <section className="py-24 md:py-32 bg-[var(--tx-bg-2)] border-y border-[var(--tx-border)]" data-testid="testimonials-section">
      <JsonLd
        id="jsonld-aggregate-rating"
        data={{
          "@context": "https://schema.org",
          "@type": "TravelAgency",
          name: SITE.name,
          url: SITE_URL,
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: REVIEW_AGGREGATE.ratingValue,
            reviewCount: reviewCount,
            bestRating: REVIEW_AGGREGATE.bestRating,
          },
          review: allReviews.map((t) => ({
            "@type": "Review",
            reviewRating: { "@type": "Rating", ratingValue: t.rating, bestRating: 5 },
            author: { "@type": "Person", name: t.name },
            reviewBody: t.quote,
          })),
        }}
      />
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <Reveal><Overline>Trusted by travel agents</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display font-light tracking-tight text-4xl sm:text-5xl lg:text-6xl">
                What our agent partners say
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="flex items-center gap-4 text-[var(--tx-text-2)]">
              <div className="flex gap-1 text-[var(--tx-gold)]">
                {Array.from({ length: 5 }).map((_, k) => (<Star key={k} className="h-5 w-5 fill-current" />))}
              </div>
              <span className="text-sm">
                <span className="text-[var(--tx-gold)] font-medium">{REVIEW_AGGREGATE.ratingValue}/5</span> · {reviewCount} verified partner reviews
              </span>
            </div>
          </Reveal>
        </div>
        <div className="mt-14 grid gap-8 md:grid-cols-3">
          {featured.map((t, i) => (
            <Reveal key={t.id || t.name + i} delay={(i % 3) * 0.1}>
              <figure className="h-full flex flex-col border border-[var(--tx-border)] p-8 bg-[var(--tx-bg)]" data-testid={`testimonial-${i}`}>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex gap-1 text-[var(--tx-gold)]">
                    {Array.from({ length: 5 }).map((_, k) => (
                      <Star key={k} className={`h-4 w-4 ${k < t.rating ? "fill-current" : "opacity-30"}`} />
                    ))}
                  </div>
                  {t.verified && (
                    <span className="inline-flex items-center gap-1 text-[0.62rem] uppercase tracking-[0.14em] text-[var(--tx-gold)] border border-[var(--tx-gold)]/40 rounded-full px-2.5 py-1">
                      <ShieldCheck className="h-3 w-3" /> Verified partner
                    </span>
                  )}
                </div>
                <blockquote className="font-display text-xl font-light leading-snug flex-1">&ldquo;{t.quote}&rdquo;</blockquote>
                <figcaption className="mt-8">
                  <div className="text-[var(--tx-text)]">{t.name}</div>
                  <div className="text-xs uppercase tracking-[0.18em] text-[var(--tx-text-2)] mt-1">{t.role}</div>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <div className="mt-12 text-center">
            <Link
              to="/partners"
              data-testid="home-see-all-reviews"
              className="inline-flex items-center gap-2 rounded-full border border-[var(--tx-gold)] px-7 py-3 text-sm text-[var(--tx-gold)] hover:bg-[var(--tx-gold)] hover:text-[var(--tx-bg)] transition-colors duration-300"
            >
              See all partner reviews & credentials <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function JournalStrip() {
  const [guides, setGuides] = useState([]);
  useEffect(() => {
    let active = true;
    getFeaturedGuides(6).then((g) => { if (active) setGuides(g); });
    return () => { active = false; };
  }, []);
  return (
    <section className="py-24 md:py-32" data-testid="journal-section">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <Reveal><Overline>The journal</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display font-light tracking-tight text-4xl sm:text-5xl lg:text-6xl">
                Stories & food guides
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <Link to="/journal" data-testid="home-journal-all" className="inline-flex items-center gap-2 text-[var(--tx-gold)] hover:gap-3 transition-all duration-300">
              Read the journal <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
        <div className="grid gap-8 md:grid-cols-3">
          {guides.map((a, i) => (
            <Reveal key={a.slug} delay={(i % 3) * 0.1}>
              <Link to={`/journal/${a.slug}`} data-testid={`home-journal-${a.slug}`} className="group block">
                <div className="relative h-64 overflow-hidden">
                  <img src={a.image} alt={a.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                </div>
                <div className="mt-5 flex items-center gap-3 text-xs uppercase tracking-[0.18em] text-[var(--tx-text-2)]">
                  <span className="text-[var(--tx-gold)]">{a.category}</span> · {a.read}
                </div>
                <h3 className="mt-3 font-display text-2xl font-light leading-snug group-hover:text-[var(--tx-gold)] transition-colors duration-300">{a.title}</h3>
                <p className="mt-3 text-sm text-[var(--tx-text-2)] leading-relaxed line-clamp-3">{a.excerpt}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section id="enquire" className="py-24 md:py-32 bg-[var(--tx-bg-2)] border-t border-[var(--tx-border)]" data-testid="cta-section">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal><Overline>Begin the journey</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display font-light tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Send us a brief. We&apos;ll draft the itinerary.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-[var(--tx-text-2)] leading-relaxed text-lg max-w-md">
              Send us the dates, pax and destinations and our trade desk comes back within
              24 hours with net rates and a workable itinerary — no obligation.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal delay={0.15}>
            <div className="border border-[var(--tx-border)] bg-[var(--tx-bg)] p-7 md:p-9">
              <EnquiryForm />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function TrustStrip() {
  const points = [
    { label: "Operating since", value: "2016" },
    { label: "GST registered", value: SITE.gst },
    { label: "Agent partners", value: SITE.partnerCount },
    { label: "Partner rating", value: SITE.rating },
  ];
  return (
    <section className="border-y border-[var(--tx-border)] bg-[var(--tx-bg-2)]" data-testid="home-trust-strip">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-10 md:py-12 grid gap-8 lg:grid-cols-12 items-center">
        <div className="lg:col-span-4">
          <div className="relative overflow-hidden rounded-sm">
            <img
              src={OFFICE_HERO}
              alt="Inside the TripTrix Voyages office — a unit of Vedic Destination"
              loading="lazy"
              className="h-56 w-full object-cover"
              data-testid="trust-strip-photo"
            />
            <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
              <p className="inline-flex items-center gap-2 text-xs text-white/85">
                <ShieldCheck className="h-3.5 w-3.5 text-[var(--tx-gold)]" /> Our North East operations office
              </p>
            </div>
          </div>
        </div>
        <div className="lg:col-span-8">
          <div className="flex items-center gap-2 text-[var(--tx-gold)]">
            <BadgeCheck className="h-4 w-4" />
            <span className="text-xs uppercase tracking-[0.2em]">Trusted since 2016 · a unit of {SITE.parentCompany}</span>
          </div>
          <p className="mt-4 font-display text-2xl md:text-3xl font-light leading-snug max-w-2xl">
            A registered, ground-owned DMC trusted by {SITE.partnerCount} travel agents across India.
          </p>
          <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-6">
            {points.map((p) => (
              <div key={p.label}>
                <div className="font-display text-xl md:text-2xl font-light text-[var(--tx-gold)] break-words">{p.value}</div>
                <div className="mt-1 text-xs uppercase tracking-[0.14em] text-[var(--tx-text-2)]">{p.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function DmcMesh() {
  const states = DMC_REGIONS.filter((r) => r.key !== "northeast");
  const cols = [
    { title: "DMC by state", links: DMC_REGIONS.map((r) => ({ to: r.path, label: `${r.name} DMC` })) },
    { title: "DMC & tour operator by city", links: CITY_REGIONS.map((r) => ({ to: r.path, label: `DMC in ${r.name}` })) },
    { title: "Destination hubs", links: states.map((r) => ({ to: `/${r.destSlug}-tourism`, label: `${r.name} Tourism` })) },
  ];
  return (
    <section className="py-24 md:py-32 border-t border-[var(--tx-border)]" data-testid="home-dmc-mesh">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <Reveal><Overline>For the travel trade</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 font-display font-light tracking-tight text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
            A B2B DMC across the North East, Bhutan & Nepal
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-6 max-w-2xl text-[var(--tx-text-2)] leading-relaxed">
            Net rates, permits, hotels and ground ops for travel agents — pick your destination,
            city or planning hub to get started.
          </p>
        </Reveal>
        <div className="mt-14 grid gap-10 md:grid-cols-3">
          {cols.map((col, ci) => (
            <Reveal key={col.title} delay={0.1 + ci * 0.08}>
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-[var(--tx-gold)] pb-4 border-b border-[var(--tx-border)]">{col.title}</div>
                <ul className="mt-5 space-y-3">
                  {col.links.map((l) => (
                    <li key={l.to}>
                      <Link
                        to={l.to}
                        data-testid={`mesh-link-${l.to}`}
                        className="group inline-flex items-center gap-2 text-[var(--tx-text)]/80 hover:text-[var(--tx-gold)] transition-colors duration-300"
                      >
                        <ArrowRight className="h-3.5 w-3.5 text-[var(--tx-gold)] opacity-70 group-hover:translate-x-0.5 transition-transform duration-300" />
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-12 pt-8 border-t border-[var(--tx-border)]">
            <Link
              to="/airport-transfers"
              data-testid="mesh-link-/airport-transfers"
              className="group inline-flex items-center gap-2 text-[var(--tx-gold)] hover:gap-3 transition-all duration-300"
            >
              Airport, railway & bus-stand transfers for agents <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// Homepage agent FAQs — question-shaped headings + FAQPage markup so answer
// engines and auditors can quote them directly.
const HOME_FAQS = [
  { q: "What is a DMC, and why do travel agents need one for North East India?", a: "A DMC (destination management company) is your ground team: we contract hotels, run vehicles, secure permits and manage guides in the region while you sell the trip under your brand. North East India has permits, seasonal roads and remote logistics, so a local DMC removes the operational risk for your agency." },
  { q: "How do I get net rates for North East India tour packages?", a: "Send your client's dates, group size and preferred hotel category through the enquiry form or WhatsApp. Our trade desk replies with confidential net rates and a day-wise itinerary within 24 hours on working days. There are no retail prices on this site — net rates are shared only with registered travel agents and tour operators." },
  { q: "Which destinations does TripTrix Voyages cover?", a: "All eight North East Indian states — Sikkim, Assam, Meghalaya, Arunachal Pradesh, Nagaland, Manipur, Mizoram and Tripura — plus Darjeeling, Bhutan and Nepal. We operate from Siliguri with a branch office in Guwahati, covering 11+ Himalayan destinations." },
  { q: "Do you handle permits like ILP and PAP for restricted areas?", a: "Yes. We arrange Inner Line Permits for Arunachal Pradesh, Nagaland, Mizoram and Manipur, Protected Area Permits for parts of Sikkim, and Bhutan entry formalities. Share your clients' nationalities and route and we handle the paperwork end to end." },
  { q: "Can you white-label itineraries and ground services for my clients?", a: "Every itinerary — FIT, honeymoon, family, group or MICE — is delivered white-labelled, so your client only ever sees your agency's brand while our ground team runs the trip." },
  { q: "How fast does TripTrix respond to a booking enquiry?", a: "We confirm availability and net rates within 24 hours on working days, then hold services while you close your client. While the group travels, our ground team is reachable 24/7." },
];

function HomeFaq() {
  return (
    <section className="py-24 md:py-32 border-t border-[var(--tx-border)]" data-testid="home-faq-section">
      <JsonLd
        id="jsonld-home-faq"
        data={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          datePublished: "2026-06-01",
          dateModified: "2026-09-01",
          mainEntity: HOME_FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }}
      />
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Reveal><Overline>Before you ask</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-display font-light tracking-tight text-4xl sm:text-5xl">
              Questions travel agents ask us
            </h2>
          </Reveal>
        </div>
        <div className="lg:col-span-7 lg:col-start-6 space-y-5">
          {HOME_FAQS.map((f, i) => (
            <Reveal key={f.q}>
              <div className="border border-[var(--tx-border)] p-6" data-testid={`home-faq-${i}`}>
                <h3 className="font-display text-xl font-light">{f.q}</h3>
                <p className="mt-3 text-sm text-[var(--tx-text-2)] leading-relaxed">{f.a}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  usePageMeta({
    title: "North East India DMC for Travel Agents | TripTrix Voyages",
    description:
      "TripTrix Voyages is a B2B DMC for travel agents & tour operators — net rates, permits & ground handling across North East India, Bhutan, Nepal & Darjeeling.",
    keywords:
      "North East India DMC, DMC for travel agents, B2B tour operator North East India, net rates North East India packages, Sikkim DMC, Meghalaya DMC, Bhutan DMC, Darjeeling DMC, DMC in Siliguri, best DMC in North East India for travel agents, wholesale North East tour packages, ground handling and permits North East India, white label itineraries for tour operators",
  });
  return (
    <>
      <JsonLd
        id="jsonld-org"
        data={{
          "@context": "https://schema.org",
          "@type": ["Organization", "TravelAgency"],
          "@id": `${SITE_URL}/#org`,
          name: SITE.name,
          description:
            "Ground Destination Management Company for North East India, Bhutan, Nepal and Darjeeling.",
          url: SITE_URL,
          logo: "https://images.unsplash.com/photo-1645033393637-9089ab100756?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
          email: SITE.email,
          telephone: SITE.phoneRaw,
          foundingDate: "2016",
          datePublished: "2026-06-01",
          dateModified: "2026-09-01",
          areaServed: ["North East India", "Bhutan", "Nepal", "Darjeeling"],
          address: { "@type": "PostalAddress", addressLocality: "Siliguri (Shiv Mandir)", addressRegion: "West Bengal", addressCountry: "IN" },
        }}
      />
      <Hero />
      <Stats />
      <TrustStrip />
      <Intro />
      <Marquee />
      <Destinations />
      <Manifesto />
      <Packages />
      <Testimonials />
      <DmcMesh />
      <JournalStrip />
      <HomeFaq />
      <CTA />
    </>
  );
}
